import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import {
  Target, TrendingUp, Home, RefreshCw, PieChart as PieIcon,
  FileText, Building2, CheckCircle2, AlertTriangle, Clock, ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

// ==================== Mock Data ====================

const CONTRIBUTION_MEMBERS = [
  {
    name: 'John Smith', age: 52, marginalRate: 39, currentSG: 27500, currentSS: 0, currentNCC: 0,
    recStrategy: 'Max concessional to $30,000 via salary sacrifice',
    currentTax: 10725, optimisedTax: 6750, saving: 3975,
  },
  {
    name: 'Jane Smith', age: 49, marginalRate: 34.5, currentSG: 18200, currentSS: 5000, currentNCC: 10000,
    recStrategy: 'Increase salary sacrifice to fill $30K cap, then NCC up to $110K',
    currentTax: 7935, optimisedTax: 4500, saving: 3435,
  },
  {
    name: 'Mark Johnson', age: 67, marginalRate: 39, currentSG: 0, currentSS: 0, currentNCC: 110000,
    recStrategy: 'Consider downsizer contribution ($300K eligible). Reduce NCC.',
    currentTax: 16500, optimisedTax: 0, saving: 16500,
  },
  {
    name: 'Rosa Garcia', age: 45, marginalRate: 32.5, currentSG: 22000, currentSS: 0, currentNCC: 0,
    recStrategy: 'Salary sacrifice additional $8,000 to reach $30K concessional cap',
    currentTax: 7150, optimisedTax: 4500, saving: 2650,
  },
];

const STRATEGY_CHART_DATA = CONTRIBUTION_MEMBERS.map(m => ({
  name: m.name.split(' ')[0],
  'Current Tax': m.currentTax,
  'Optimised Tax': m.optimisedTax,
}));

const DOWNSIZER_MEMBERS = [
  { name: 'Mark Johnson', age: 67, propertyYears: 15, principalRes: true, previouslyUsed: false, eligible: true, amount: 300000 },
  { name: 'Sarah Johnson', age: 64, propertyYears: 12, principalRes: true, previouslyUsed: false, eligible: true, amount: 300000 },
  { name: 'John Smith', age: 52, propertyYears: 8, principalRes: true, previouslyUsed: false, eligible: false, amount: 0 },
  { name: 'Rosa Garcia', age: 45, propertyYears: 5, principalRes: false, previouslyUsed: false, eligible: false, amount: 0 },
];

const RECONTRIBUTION = [
  {
    member: 'Mark Johnson',
    currentTaxFree: 120000, currentTaxable: 380000, totalBalance: 500000,
    withdrawAmount: 380000,
    afterTaxFree: 500000, afterTaxable: 0,
    taxSavingOnDeath: 60800,
  },
  {
    member: 'Sarah Johnson',
    currentTaxFree: 200000, currentTaxable: 300000, totalBalance: 500000,
    withdrawAmount: 300000,
    afterTaxFree: 500000, afterTaxable: 0,
    taxSavingOnDeath: 48000,
  },
];

const SEGREGATION_COMPARISON = [
  { method: 'Segregated', exemptPct: 100, taxSaving: 18200, actuarialCost: 0, recommended: true },
  { method: 'Unsegregated (Actuarial)', exemptPct: 62, taxSaving: 11284, actuarialCost: 1500, recommended: false },
];

const ESTATE_MEMBERS = [
  {
    member: 'John Smith', bdbStatus: 'current' as const, bdbExpiry: '15/03/2028', reversionary: true,
    dependants: ['Jane Smith (spouse)', 'Tom Smith (child, 24)'],
    risks: [],
  },
  {
    member: 'Jane Smith', bdbStatus: 'current' as const, bdbExpiry: '15/03/2028', reversionary: true,
    dependants: ['John Smith (spouse)', 'Tom Smith (child, 24)'],
    risks: [],
  },
  {
    member: 'Mark Johnson', bdbStatus: 'expired' as const, bdbExpiry: '01/01/2025', reversionary: false,
    dependants: ['Sarah Johnson (spouse)', 'Lisa Johnson (child, 35, non-dependant)'],
    risks: ['BDBN expired', 'No reversionary beneficiary set', 'Non-dependant adult child - tax implications'],
  },
  {
    member: 'Rosa Garcia', bdbStatus: 'current' as const, bdbExpiry: '20/06/2027', reversionary: false,
    dependants: ['Carlos Garcia (spouse)'],
    risks: ['No reversionary beneficiary set'],
  },
];

const LRBA_CHECKLIST = [
  { step: 1, title: 'Identify Property', description: 'Select suitable property that meets investment strategy and sole purpose test', status: 'completed' as const },
  { step: 2, title: 'Establish Bare Trust', description: 'Set up a separate bare trust (holding trust) to hold legal title', status: 'completed' as const },
  { step: 3, title: 'Loan Arrangement (PCG 2016/5)', description: 'Ensure loan terms comply with safe harbour provisions - benchmark interest rate, LVR limits, loan term', status: 'in-progress' as const },
  { step: 4, title: 'Related Party Assessment', description: 'Determine if lender is a related party. Apply arms-length terms per PCG 2016/5', status: 'not-started' as const },
  { step: 5, title: 'Compliance Checks', description: 'Verify: single acquirable asset, no improvements beyond repair, adequate insurance, investment strategy updated', status: 'not-started' as const },
];

// ==================== Component ====================

export default function SMSFAdvisoryPage() {
  const [expandedCase, setExpandedCase] = useState<number | null>(null);

  // --- API Queries ---
  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => { const r = await apiClient.get('/smsf/funds'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const firstFundId = fundsData?.[0]?.id;

  const { data: apiMembers, isLoading: membersLoading } = useQuery({
    queryKey: ['smsf-fund-members', firstFundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${firstFundId}/members`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!firstFundId,
  });

  const { data: apiContributions, isLoading: contribLoading } = useQuery({
    queryKey: ['smsf-fund-contributions', firstFundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${firstFundId}/contributions`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!firstFundId,
  });

  const { data: apiAssets, isLoading: assetsLoading } = useQuery({
    queryKey: ['smsf-fund-assets', firstFundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${firstFundId}/assets`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!firstFundId,
  });

  const { data: apiBdbn, isLoading: bdbnLoading } = useQuery({
    queryKey: ['smsf-fund-bdbn', firstFundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${firstFundId}/bdbn`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!firstFundId,
  });

  // Use API data with mock fallback
  const contributionMembers = apiContributions || CONTRIBUTION_MEMBERS;
  const estateMembers = apiBdbn || ESTATE_MEMBERS;
  const isDataLoading = fundsLoading || membersLoading || contribLoading || assetsLoading || bdbnLoading;

  const strategyChartData = (apiContributions || CONTRIBUTION_MEMBERS).map((m: any) => ({
    name: (m.name || '').split(' ')[0],
    'Current Tax': m.currentTax,
    'Optimised Tax': m.optimisedTax,
  }));

  return (
    <div className="space-y-6">
      <PageHeader icon={Target} title="SMSF Advisory & Strategy" />

      {isDataLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      <Tabs defaultValue="contribution-optimiser">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="contribution-optimiser"><TrendingUp className="h-3.5 w-3.5 mr-1.5" />Contribution Optimiser</TabsTrigger>
          <TabsTrigger value="downsizer"><Home className="h-3.5 w-3.5 mr-1.5" />Downsizer</TabsTrigger>
          <TabsTrigger value="recontribution"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Re-contribution</TabsTrigger>
          <TabsTrigger value="segregation"><PieIcon className="h-3.5 w-3.5 mr-1.5" />Segregation</TabsTrigger>
          <TabsTrigger value="estate"><FileText className="h-3.5 w-3.5 mr-1.5" />Estate Planning</TabsTrigger>
          <TabsTrigger value="lrba"><Building2 className="h-3.5 w-3.5 mr-1.5" />LRBA Property</TabsTrigger>
        </TabsList>

        {/* ===== Contribution Optimiser ===== */}
        <TabsContent value="contribution-optimiser" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Member Contribution Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium text-center">Age</th>
                      <th className="pb-2 pr-4 font-medium text-center">Marginal Rate</th>
                      <th className="pb-2 pr-4 font-medium text-right">Current SG</th>
                      <th className="pb-2 pr-4 font-medium text-right">Salary Sacrifice</th>
                      <th className="pb-2 pr-4 font-medium text-right">NCC</th>
                      <th className="pb-2 font-medium">Recommended Strategy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contributionMembers.map((m: any, i: number) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 font-medium">{m.name}</td>
                        <td className="py-2.5 pr-4 text-center">{m.age}</td>
                        <td className="py-2.5 pr-4 text-center">{m.marginalRate}%</td>
                        <td className="py-2.5 pr-4 text-right">${(m.currentSG ?? 0).toLocaleString('en-AU')}</td>
                        <td className="py-2.5 pr-4 text-right">${(m.currentSS ?? 0).toLocaleString('en-AU')}</td>
                        <td className="py-2.5 pr-4 text-right">${(m.currentNCC ?? 0).toLocaleString('en-AU')}</td>
                        <td className="py-2.5 text-xs text-muted-foreground">{m.recStrategy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Tax Savings Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-4 font-medium">Member</th>
                        <th className="pb-2 pr-4 font-medium text-right">Current Tax</th>
                        <th className="pb-2 pr-4 font-medium text-right">Optimised Tax</th>
                        <th className="pb-2 font-medium text-right">Saving</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contributionMembers.map((m: any, i: number) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2.5 pr-4 font-medium">{m.name}</td>
                          <td className="py-2.5 pr-4 text-right text-red-600">${(m.currentTax ?? 0).toLocaleString('en-AU')}</td>
                          <td className="py-2.5 pr-4 text-right text-emerald-600">${(m.optimisedTax ?? 0).toLocaleString('en-AU')}</td>
                          <td className="py-2.5 text-right font-bold text-emerald-600">${(m.saving ?? 0).toLocaleString('en-AU')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={strategyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} />
                      <Tooltip formatter={(v) => `$${Number(v).toLocaleString('en-AU')}`} cursor={false} />
                      <Legend />
                      <Bar dataKey="Current Tax" fill="#ef4444" radius={[4, 4, 0, 0]} activeBar={false}/>
                      <Bar dataKey="Optimised Tax" fill="#10b981" radius={[4, 4, 0, 0]} activeBar={false}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Downsizer ===== */}
        <TabsContent value="downsizer" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Downsizer Contribution Eligibility</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {DOWNSIZER_MEMBERS.map((m, i) => (
                <Card key={i} className={cn('border', m.eligible ? 'border-emerald-200 bg-emerald-50/30' : 'border-muted')}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-start justify-between mb-3">
                      <p className="text-sm font-semibold">{m.name}</p>
                      <Badge variant={m.eligible ? 'default' : 'destructive'}>
                        {m.eligible ? 'Eligible' : 'Not Eligible'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <p className="text-muted-foreground">Age (min 55)</p>
                        <p className={cn('font-medium', m.age >= 55 ? 'text-emerald-600' : 'text-red-600')}>
                          {m.age >= 55 ? <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" /> : <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />}
                          {m.age} years
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Owned 10+ years</p>
                        <p className={cn('font-medium', m.propertyYears >= 10 ? 'text-emerald-600' : 'text-red-600')}>
                          {m.propertyYears >= 10 ? <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" /> : <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />}
                          {m.propertyYears} years
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Principal Residence</p>
                        <p className={cn('font-medium', m.principalRes ? 'text-emerald-600' : 'text-red-600')}>
                          {m.principalRes ? <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" /> : <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />}
                          {m.principalRes ? 'Yes' : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Previously Used</p>
                        <p className={cn('font-medium', !m.previouslyUsed ? 'text-emerald-600' : 'text-red-600')}>
                          {!m.previouslyUsed ? <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" /> : <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />}
                          {m.previouslyUsed ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>
                    {m.eligible && (
                      <p className="text-sm font-medium mt-3 text-emerald-700">
                        Eligible amount: up to ${m.amount.toLocaleString('en-AU')} per member
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Re-contribution Strategy ===== */}
        <TabsContent value="recontribution" className="space-y-4">
          {RECONTRIBUTION.map((r, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{r.member} - Re-contribution Strategy</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Before</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Tax-Free Component</span><span className="font-medium">${r.currentTaxFree.toLocaleString('en-AU')}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Taxable Component</span><span className="font-medium text-red-600">${r.currentTaxable.toLocaleString('en-AU')}</span></div>
                      <div className="flex justify-between border-t pt-1"><span className="font-medium">Total Balance</span><span className="font-bold">${r.totalBalance.toLocaleString('en-AU')}</span></div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">After Re-contribution</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Tax-Free Component</span><span className="font-medium text-emerald-600">${r.afterTaxFree.toLocaleString('en-AU')}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Taxable Component</span><span className="font-medium text-emerald-600">${r.afterTaxable.toLocaleString('en-AU')}</span></div>
                      <div className="flex justify-between border-t pt-1"><span className="font-medium">Total Balance</span><span className="font-bold">${r.totalBalance.toLocaleString('en-AU')}</span></div>
                    </div>
                  </div>
                </div>
                <Card className="mt-4 bg-emerald-50/50 border-emerald-200">
                  <CardContent className="pt-3 pb-3">
                    <p className="text-sm">
                      <span className="font-semibold text-emerald-700">Potential tax saving on death benefit to non-dependant: </span>
                      <span className="font-bold text-emerald-700">${r.taxSavingOnDeath.toLocaleString('en-AU')}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Withdraw ${r.withdrawAmount.toLocaleString('en-AU')} (unrestricted non-preserved) and re-contribute as NCC to convert taxable component to tax-free.
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ===== Segregation ===== */}
        <TabsContent value="segregation" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Segregated vs Unsegregated ECPI Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Method</th>
                      <th className="pb-2 pr-4 font-medium text-center">Exempt %</th>
                      <th className="pb-2 pr-4 font-medium text-right">Tax Saving</th>
                      <th className="pb-2 pr-4 font-medium text-right">Actuarial Cost</th>
                      <th className="pb-2 font-medium text-center">Recommended</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SEGREGATION_COMPARISON.map((s, i) => (
                      <tr key={i} className={cn('border-b last:border-0', s.recommended && 'bg-emerald-50/30')}>
                        <td className="py-2.5 pr-4 font-medium">{s.method}</td>
                        <td className="py-2.5 pr-4 text-center">{s.exemptPct}%</td>
                        <td className="py-2.5 pr-4 text-right font-medium text-emerald-600">${s.taxSaving.toLocaleString('en-AU')}</td>
                        <td className="py-2.5 pr-4 text-right">${s.actuarialCost.toLocaleString('en-AU')}</td>
                        <td className="py-2.5 text-center">
                          {s.recommended && <Badge variant="default">Recommended</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm font-semibold text-blue-700 mb-1">Recommendation</p>
              <p className="text-sm text-blue-800">
                Based on the fund's current structure with 3 pension members and dedicated pension assets, the <span className="font-semibold">segregated method</span> is
                recommended. This provides 100% ECPI on segregated pension assets, saving an additional ${(18200 - 11284 + 1500).toLocaleString('en-AU')} compared
                to the unsegregated approach (including actuarial certificate costs).
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Estate Planning ===== */}
        <TabsContent value="estate" className="space-y-4">
          {estateMembers.map((m: any, i: number) => (
            <Card key={i} className={cn(m.risks.length > 0 && 'border-amber-200')}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold">{m.member}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Dependants: {m.dependants.join(', ')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant={m.bdbStatus === 'current' ? 'default' : 'destructive'}>
                      BDBN: {m.bdbStatus === 'current' ? 'Current' : 'Expired'}
                    </Badge>
                    <Badge variant={m.reversionary ? 'default' : 'outline'}>
                      Reversionary: {m.reversionary ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>
                {m.bdbStatus === 'current' && (
                  <p className="text-xs text-muted-foreground mb-2">BDBN expires: {m.bdbExpiry}</p>
                )}
                {m.risks.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {m.risks.map((risk: string, ri: number) => (
                      <div key={ri} className="flex items-center gap-2 text-xs text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        <span>{risk}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Action Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { text: 'Renew BDBN for Mark Johnson (expired 01/01/2025)', urgent: true },
                  { text: 'Set up reversionary pension nomination for Mark Johnson', urgent: true },
                  { text: 'Set up reversionary pension nomination for Rosa Garcia', urgent: false },
                  { text: 'Review death benefit tax implications for non-dependant Lisa Johnson', urgent: false },
                  { text: 'Schedule annual estate planning review for all members', urgent: false },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="rounded" readOnly />
                    <span className={cn(item.urgent && 'text-red-600 font-medium')}>{item.text}</span>
                    {item.urgent && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Urgent</Badge>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== LRBA Property ===== */}
        <TabsContent value="lrba" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">LRBA Setup Checklist</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {LRBA_CHECKLIST.map(step => (
                  <div key={step.step} className="flex items-start gap-4 p-3 rounded-lg border">
                    <div className={cn(
                      'h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold',
                      step.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      step.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-500'
                    )}>
                      {step.status === 'completed' ? <CheckCircle2 className="h-4 w-4" /> : step.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">{step.title}</p>
                        <Badge variant={
                          step.status === 'completed' ? 'default' :
                          step.status === 'in-progress' ? 'secondary' : 'outline'
                        }>
                          {step.status === 'completed' ? 'Completed' : step.status === 'in-progress' ? 'In Progress' : 'Not Started'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
